import { useContext, useState, useEffect, useRef } from 'react';
import { ThemeContext } from '~/lib/ThemeContext';
import { formatCurrency } from '~/lib/CurrencyHelpers';
import { formatDateShort } from '~/lib/DateTime';
import { PrimaryColor } from '~/lib/Styles';
import Toggle from '~/components/Toggle.jsx';
import OptionToggle from '~/components/OptionToggle.jsx';

function getUrlFromImage(image, small) {
    if (!image) {
        return '';
    }

    if (image.includes('unsplash.com')) {
        return `${image}&w=${small ? '400' : '900'}`;
    }

    return image;
}

const PrintableGiftCertificate = ({
    className,
    small,
    print,
    business,
    recipient,
                                      purchaserName,
    message,
    image,
    value,
    remainingValue,
    expires,
    code,
}) => {
    const { color } = useContext(ThemeContext);
    const [printGreyscaleToggle, setPrintGreyscaleToggle] = useState(false);
    const [imageAspectRatio, setImageAspectRatio] = useState(null);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [containerWidth, setContainerWidth] = useState(null);
    const containerRef = useRef();

    const greyscale = printGreyscaleToggle && print;

    const backgroundUrl =
        image && !greyscale ? getUrlFromImage(image, small) : '';

    const dropShadow = greyscale ? '' : 'drop-shadow';

    // Observe container width changes
    useEffect(() => {
        if (!containerRef.current) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                setContainerWidth(entry.contentRect.width);
            }
        });

        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    // Load image and calculate aspect ratio
    useEffect(() => {
        if (!backgroundUrl) {
            setImageAspectRatio(null);
            setImageLoaded(false);
            return;
        }

        const img = new Image();
        img.onload = () => {
            const aspectRatio = img.width / img.height;
            setImageAspectRatio(aspectRatio);
            setImageLoaded(true);
        };
        img.onerror = () => {
            setImageAspectRatio(null);
            setImageLoaded(false);
        };
        img.src = backgroundUrl;
    }, [backgroundUrl]);

    // Calculate container dimensions based on image aspect ratio
    const getContainerStyle = () => {
        // Define max dimensions
        // For non-small mode, use the container's width if available
        const maxWidth = small ? 500 : (containerWidth || 650);
        const maxHeight = small ? 300 : 500;

        const baseStyle = {
            backgroundColor: greyscale ? '#FFFFFF' : color || PrimaryColor,
            backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            minHeight: `${maxHeight*0.75}px`,
        };

        if (!imageAspectRatio || !imageLoaded) {
            return baseStyle;
        }

        let width, height;

        // Calculate dimensions based on aspect ratio
        if (imageAspectRatio > 1) {
            // Landscape image
            width = maxWidth;
            height = width / imageAspectRatio;
            if (height > maxHeight) {
                height = maxHeight;
                width = height * imageAspectRatio;
            }
        } else {
            // Portrait or square image
            height = maxHeight;
            width = height * imageAspectRatio;
            if (width > maxWidth) {
                width = maxWidth;
                height = width / imageAspectRatio;
            }
        }

        return {
            ...baseStyle,
            width: `${width}px`,
            height: `${height}px`,
            maxWidth: `${maxWidth}px`,
            maxHeight: `${maxHeight}px`,
        };
    };

    return (
        <div ref={containerRef}>
            {!!print && (
                <label className="text-center mb-4 flex items-center text-gray-500 print:hidden">
                    <Toggle
                        enabled={printGreyscaleToggle}
                        setEnabled={setPrintGreyscaleToggle}
                        className="mr-2"
                    />
                    Print black and white
                </label>
            )}
            <div
                className={`rounded-xl ${small ? 'text-base p-6' : 'text-xl p-10'} flex flex-col justify-between ${greyscale ? 'text-black border border-gray-400 border-2 border-dashed' : 'text-white'} ${className}`}
                style={getContainerStyle()}
            >
                <div className="flex items-start justify-between">
                    <div className={`${dropShadow} max-w-1/2`}>
                        <div className="font-bold">To: <span className="font-semibold">{recipient.name}</span></div>


                        {!!purchaserName && (
                            <>
                                <div className="font-bold mt-2">From: <span className="font-semibold">
                                    {purchaserName}
                                </span></div>
                            </>
                        )}
                    </div>
                    <div
                        className={`${dropShadow} ${small ? 'text-[40px]' : 'text-[80px]'}`}
                    >
                        {formatCurrency(value)}
                        {value != remainingValue &&
                            typeof remainingValue !== 'undefined' && (
                                <div className="text-lg text-right mt-8">
                                    {formatCurrency(remainingValue)} remaining
                                </div>
                            )}
                    </div>
                </div>

                {!!message && (
                    <div className={`${dropShadow} font-semibold`}>
                        {message}
                    </div>
                )}

                <div className="flex items-start justify-between">
                    <div className={`${dropShadow} font-semibold`}>
                        {business?.name}
                    </div>
                    <div
                        className={`${dropShadow} ${small ? 'text-base' : 'text-lg'} font-semibold`}
                    >
                        <div>Code: {code}</div>
                        <div>
                            Expires:{' '}
                            {expires ? formatDateShort(expires) : 'Never'}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrintableGiftCertificate;
